import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useFormReadOnly } from '../../../../../../common';
import { onboardingQuestionnaireActions } from '../../../../../../../store';
import useSecondaryInsuranceState from './useSecondaryInsuranceState';

import type { AppDispatch } from '../../../../../../../store';
import type { SecondaryInsuranceToggleDependencies } from './types';

export default function useSecondaryInsuranceToggle() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const isReadOnly = useFormReadOnly();
  const { noSecondaryInsurance, hasSavedSecondaryInsurance } = useSecondaryInsuranceState();

  return useSecondaryInsuranceToggleWithDependencies({
    actions: onboardingQuestionnaireActions,
    dispatch,
    getAccessTokenSilently,
    hasSavedSecondaryInsurance,
    isReadOnly,
    noSecondaryInsurance,
    onboardingQuestionnaireId,
  });
}

export function useSecondaryInsuranceToggleWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  hasSavedSecondaryInsurance,
  isReadOnly,
  noSecondaryInsurance,
  onboardingQuestionnaireId,
}: SecondaryInsuranceToggleDependencies) {

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const checked = pendingValue ?? noSecondaryInsurance;

  useEffect(() => {
    if (isReadOnly) {
      setIsConfirmOpen(false);
    }
  }, [isReadOnly]);

  const applyNoSecondaryInsurance = useCallback(
    async (nextValue: boolean, shouldDelete: boolean) => {
      if (!onboardingQuestionnaireId || isReadOnly) {
        return;
      }

      setIsProcessing(true);
      setPendingValue(nextValue);

      try {
        await dispatch(
          actions.setOnboardingQuestionnaireNoSecondaryInsurance({
            onboardingQuestionnaireId,
            noSecondaryInsurance: nextValue,
            getAccessTokenSilently,
          }),
        ).unwrap();

        if (shouldDelete) {
          await dispatch(
            actions.deleteOnboardingQuestionnaireSecondaryInsurance({
              onboardingQuestionnaireId,
              getAccessTokenSilently,
            }),
          ).unwrap();
        }
      } catch {
        // Error toast is raised by the slice; the refetch below resyncs to server truth.
      } finally {
        await dispatch(
          actions.getOnboardingQuestionnaireById({
            onboardingQuestionnaireId,
            getAccessTokenSilently,
          }),
        );
        setPendingValue(null);
        setIsProcessing(false);
        setIsConfirmOpen(false);
      }
    },
    [actions, dispatch, getAccessTokenSilently, onboardingQuestionnaireId, isReadOnly],
  );

  const onCheckboxChange = useCallback(
    (_event: Event, nextChecked: boolean) => {
      if (isReadOnly) {
        return;
      }
      if (nextChecked && hasSavedSecondaryInsurance) {
        setIsConfirmOpen(true);
        return;
      }
      applyNoSecondaryInsurance(nextChecked, false);
    },
    [applyNoSecondaryInsurance, hasSavedSecondaryInsurance, isReadOnly],
  );

  const onConfirm = useCallback(
    () => applyNoSecondaryInsurance(true, true),
    [applyNoSecondaryInsurance],
  );

  const onCancel = useCallback(() => setIsConfirmOpen(false), []);

  return { checked, isProcessing, isConfirmOpen, onCheckboxChange, onConfirm, onCancel };
}
